import './OkulyonetimProductList.scss'

import {FilterOutlined} from '@ant-design/icons'
import {Button, Col, Collapse, Pagination, Row, Select, TreeDataNode} from 'antd'
import React, {useContext, useEffect, useState} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import AppContext from '../../../context/AppContext'
import {OkulYonetimUrunFiltre, useOkulyonetimrunlerQuery} from '../../../gql/GeneratedGraphqlTypes'
import useMediaQuery from '../../../hooks/useMediaQuery'
import Spinner from '../../spinner/Spinner'
import OkulyonetimAlert from '../okulyonetim-alert/OkulyonetimAlert'
import OkulyonetimCategoryFilter from '../okulyonetim-filters/okulyonetim-category-filter/OkulyonetimCategoryFilter'
import OkulyonetimDersFilter from '../okulyonetim-filters/okulyonetim-ders-filter/OkulyonetimDersFilter'
import OkulyonetimOtherFilter from '../okulyonetim-filters/okulyonetim-other-filter/OkulyonetimOtherFilter'
import OkulyonetimSinifFilter from '../okulyonetim-filters/okulyonetim-sinif-filter/OkulyonetimSinifFilter'
import OkulyonetimProduct from '../okulyonetim-product/OkulyonetimProduct'

export interface CustomTreeDataNode extends TreeDataNode {
    parentKey?: React.Key
}
export const updateQueryParam = (
    newParams: Record<string, string | null>,
    location: ReturnType<typeof useLocation>,
    navigate: ReturnType<typeof useNavigate>
) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(newParams).forEach(([key, value]) => {
        if (value === null || value === '') {
            searchParams.delete(key);
        } else {
            searchParams.set(key, value);
        }
    });

    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
};

const OkulyonetimProductList = () => {
    const {slug} = useContext(AppContext)
    const navigate = useNavigate()
    const isMobile = useMediaQuery(992)
    const location = useLocation()
    const getLastSlugPart = (): string | undefined => {
        const pathname = window.location.pathname
        const slugPart = pathname.split('/').pop()
        return slugPart
    }

    const lastSlugPart = getLastSlugPart()

    const [filters, setFilters] = useState<OkulYonetimUrunFiltre>({
        tip_slug: lastSlugPart || '',
        kategori_slug: [], // Kategori filtresi sıfırlanıyor
        yayinevi_slug: [], // Yayinevi filtresi sıfırlanıyor
        ders_slug: [], // Ders filtresi sıfırlanıyor
        alt_kategori_slug: [],
        sub_alt_kategori_slug: [],
        sinif_slug: [], // Sınıf filtresi sıfırlanıyor
        sayfa: 0, // Sayfa sıfırlanıyor
        orderBy: 0, // Sayfa sıfırlanıyor
        yeni_mufredat: false,
    })
    const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]) // Seçili kategorilerin anahtarları
    const [checkedYayineviKeys, setCheckedYayineviKeys] = useState<React.Key[]>([]) // Seçili yayınevlerinin anahtarları
    const [checkedSinifKeys, setCheckedSinifKeys] = useState<React.Key[]>([]) // Seçili sınıfların anahtarları
    const [checkedDersKeys, setCheckedDersKeys] = useState<React.Key[]>([]) // Seçili derslerin anahtarları
    const [currentPage, setCurrentPage] = useState(1)
    const {data, loading, error, refetch} = useOkulyonetimrunlerQuery({
        variables: {
            slug: slug!,
            filtre: filters,
        },
    })

    useEffect(() => {
        if (lastSlugPart) {
            setFilters({
                tip_slug: lastSlugPart, // Yeni tip_slug atanıyor
                kategori_slug: [], // Kategori filtresi sıfırlanıyor
                yayinevi_slug: [], // Yayinevi filtresi sıfırlanıyor
                ders_slug: [], // Ders filtresi sıfırlanıyor
                sinif_slug: [], // Sınıf filtresi sıfırlanıyor
                sub_alt_kategori_slug: [],
                sayfa: 0, // Sayfa sıfırlanıyor
                orderBy: 0,
                yeni_mufredat: false, // Yeni müfredat filtresi sıfırlanıyor
            })
            setCurrentPage(1) // Sayfa numarası sıfırlanıyor
        }
    }, [lastSlugPart])

    useEffect(() => {
        if (filters) {
            refetch({filtre: filters})
        }
    }, [filters, refetch])

    useEffect(() => {
        if (error) {
            console.error(error)
            navigate('/')
        }
    }, [error, navigate])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const parseSlugArray = (key: string): string[] =>
            params.get(key)?.split(',').filter((val) => val.trim() !== '') || [];
        const sayfa0 = Number(params.get('sayfa')) || 0;  // 0-based
        const uiPage = sayfa0 + 1;                        // 1-based
        setCurrentPage(uiPage);
        setFilters((prev) => ({
            ...prev,
            kategori_slug: parseSlugArray('kategori_slug'),
            alt_kategori_slug: parseSlugArray('alt_kategori_slug'),
            sub_alt_kategori_slug: parseSlugArray('sub_alt_kategori_slug'),
            sinif_slug: parseSlugArray('sinif_slug'),
            ders_slug: parseSlugArray('ders_slug'),
            yayinevi_slug: parseSlugArray('yayinevi_slug'),
            yeni_mufredat: params.get('yeni_mufredat') === 'true',
            sayfa: sayfa0
        }));
    }, []);


    const handlePageChange = (page: number) => {
        setCurrentPage(page)
        const sayfa0 = page - 1;
        setFilters((prevFilters) => ({
            ...prevFilters,
            sayfa: sayfa0,
        }))
        updateQueryParam(
            { sayfa: String(sayfa0) },
            location,
            navigate
        );
    }
    const buildCategoryTree = (categories: any[]): CustomTreeDataNode[] => {
        const categoryMap: { [key: string]: CustomTreeDataNode } = {}
        const tree: CustomTreeDataNode[] = []

        // 1) Parent kategoriler
        categories.forEach((category) => {
            const node: CustomTreeDataNode = {
                key: category.kategori_slug,
                title: category.isim || 'Bilinmeyen Kategori',
                children: [],
            }
            categoryMap[category.kategori_slug] = node
            tree.push(node)
        })

        // 2) Alt + Sub-Alt bağla
        categories.forEach((category) => {
            const parent = categoryMap[category.kategori_slug]
            if (!parent) return

            if (category.alt_kategoriler && category.alt_kategoriler.length > 0) {
                category.alt_kategoriler.forEach((alt: any, altIndex: number) => {
                    const altKey = `${alt.alt_kategori_slug}*ALT*${altIndex}*${category.kategori_slug}`

                    const altNode: CustomTreeDataNode = {
                        key: altKey,
                        title: alt.isim,
                        parentKey: category.kategori_slug,
                        children: [],
                    }

                    // sub-alt'lar
                    if (alt.sub_alt_kategoriler && alt.sub_alt_kategoriler.length > 0) {
                        alt.sub_alt_kategoriler.forEach((sub: any, subIndex: number) => {
                            const subKey = `${sub.sub_alt_kategori_slug}*SUB*${subIndex}*${alt.alt_kategori_slug}*${altIndex}*${category.kategori_slug}`

                            const subNode: CustomTreeDataNode = {
                                key: subKey,
                                title: sub.isim,
                                parentKey: altKey, // ✅ parent artık altKey
                                children: [],
                            }

                            altNode.children!.push(subNode)
                        })
                    }

                    parent.children!.push(altNode)
                })
            }
        })

        return tree
    }

    const formatTreeData = (): TreeDataNode[] => {
        const categories = [...(data?.okul?.okulyonetimUrunler?.kategoriler || [])] // `readonly` özelliğini kaldırır
        return buildCategoryTree(categories)
    }
    if (loading) {
        return <Spinner showSpinner={loading} color="red"/>
    }
    const isAnyFilterActive = () => {
        // Filtrelerden herhangi biri doluysa true döner
        return (
            filters?.kategori_slug?.length! > 0 ||
            filters?.alt_kategori_slug?.length! > 0 ||
            filters?.sub_alt_kategori_slug?.length! > 0 ||
            filters?.yayinevi_slug?.length! > 0 ||
            filters?.ders_slug?.length! > 0 ||
            filters?.sinif_slug?.length! > 0
        )
    }

    function getSelectValueFromOrderBy(orderBy: number): string {
        switch (orderBy) {
            case 1:
                return 'fiyatArtan'
            case 2:
                return 'fiyatAzalan'
            case 3:
                return 'isimArtan'
            case 4:
                return 'isimAzalan'
            default:
                return 'onerilen'
        }
    }
    const parseNodeKey = (key: React.Key) => {
        const s = String(key)
        const parts = s.split('*')
        // Kategori ise: parts.length === 1
        if (parts.length === 1) return { type: 'KAT' as const, slug: s }

        const slug0 = parts[0]
        const kind = parts[1] // ALT | SUB (bizim format)
        return { type: kind as 'ALT' | 'SUB', slug: slug0, parts }
    }

    return (
        <div className="okulyonetim-product-list">
            <OkulyonetimAlert/>
            <Row gutter={[24, 24]}>
                <Col xs={24} lg={6}>

                    {isMobile ? (
                        <div className="okulyonetim-product-list-filter">
                            <Collapse
                                size="large"
                                expandIcon={() => (
                                    <div className="okulyonetim-product-list-filter-icon">
                                        <FilterOutlined/>
                                    </div>
                                )}
                            >
                                <Collapse.Panel key={1} header={'Filtrele'}>
                                    <OkulyonetimCategoryFilter
                                        treeData={formatTreeData() as CustomTreeDataNode[]}
                                        checkedKeys={checkedKeys} // Seçili kategoriler
                                        onCheck={(updatedCheckedKeys, info) => {
                                            let kategoriSluglar: string[] = Array.isArray(filters?.kategori_slug) ? [...filters.kategori_slug] : []
                                            let altKategoriSluglar: string[] = Array.isArray(filters?.alt_kategori_slug) ? [...filters.alt_kategori_slug] : []
                                            let subAltKategoriSluglar: string[] = Array.isArray(filters?.sub_alt_kategori_slug) ? [...filters.sub_alt_kategori_slug] : []

                                            const nodeKeyStr = String(info.node.key)
                                            const parsed = parseNodeKey(info.node.key)

                                            if (info.checked) {
                                                if (info.node.children && info.node.children.length > 0) {
                                                    // Parent node seçildi (Kategori veya Alt)
                                                    if (parsed.type === 'KAT') {
                                                        // Kategori seçildi -> kategori + alt + varsa sub-alt ekle
                                                        kategoriSluglar = Array.from(new Set([...kategoriSluglar, nodeKeyStr]))

                                                        const altKeys = info.node.children.map((child: any) => String(child.key))
                                                        const altSlugs = altKeys.map((k: React.Key) => parseNodeKey(k).slug)
                                                        altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, ...altSlugs]))

                                                        // sub-alt children varsa hepsini topla
                                                        const subSlugs: string[] = []
                                                        info.node.children.forEach((altNode: any) => {
                                                            if (altNode.children?.length) {
                                                                altNode.children.forEach((subNode: any) => {
                                                                    subSlugs.push(parseNodeKey(subNode.key).slug)
                                                                })
                                                            }
                                                        })
                                                        subAltKategoriSluglar = Array.from(new Set([...subAltKategoriSluglar, ...subSlugs]))
                                                    } else if (parsed.type === 'ALT') {
                                                        // Alt kategori node seçildi -> alt + parent kategori + sub-alt children
                                                        const altSlug = parsed.slug
                                                        altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, altSlug]))

                                                        if (info.node.parentKey) {
                                                            kategoriSluglar = Array.from(new Set([...kategoriSluglar, String(info.node.parentKey)]))
                                                        }

                                                        const subSlugs = (info.node.children || []).map((c: any) => parseNodeKey(c.key).slug)
                                                        subAltKategoriSluglar = Array.from(new Set([...subAltKategoriSluglar, ...subSlugs]))
                                                    }
                                                } else {
                                                    // Leaf seçildi (ALT leaf veya SUB leaf)
                                                    if (parsed.type === 'SUB') {
                                                        const subSlug = parsed.slug
                                                        const parts = parsed.parts
                                                        const parentAltSlug = parts[3]
                                                        const parentKatSlug = parts[5]

                                                        subAltKategoriSluglar = Array.from(new Set([...subAltKategoriSluglar, subSlug]))
                                                        altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, parentAltSlug]))
                                                        kategoriSluglar = Array.from(new Set([...kategoriSluglar, parentKatSlug]))
                                                    } else {
                                                        // ALT leaf (sub yok)
                                                        const altSlug = nodeKeyStr.split('*')[0]
                                                        altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, altSlug]))

                                                        if (info.node.parentKey) {
                                                            kategoriSluglar = Array.from(new Set([...kategoriSluglar, String(info.node.parentKey)]))
                                                        }
                                                    }
                                                }
                                            } else {
                                                if (info.node.children && info.node.children.length > 0) {
                                                    // Parent kaldırıldı (Kategori veya Alt)
                                                    if (parsed.type === 'KAT') {
                                                        const katSlug = nodeKeyStr
                                                        kategoriSluglar = kategoriSluglar.filter(x => x !== katSlug)

                                                        // Bu kategorinin altlarını ve sublarını da kaldır
                                                        const altKeys = info.node.children.map((child: any) => String(child.key))
                                                        const altSlugs = altKeys.map((k: React.Key) => parseNodeKey(k).slug)
                                                        altKategoriSluglar = altKategoriSluglar.filter(x => !altSlugs.includes(x))

                                                        const subSlugs: string[] = []
                                                        info.node.children.forEach((altNode: any) => {
                                                            if (altNode.children?.length) {
                                                                altNode.children.forEach((subNode: any) => subSlugs.push(parseNodeKey(subNode.key).slug))
                                                            }
                                                        })
                                                        subAltKategoriSluglar = subAltKategoriSluglar.filter(x => !subSlugs.includes(x))
                                                    } else if (parsed.type === 'ALT') {
                                                        const altSlug = parsed.slug
                                                        altKategoriSluglar = altKategoriSluglar.filter(x => x !== altSlug)

                                                        // Altın sub-altlarını da kaldır
                                                        const subSlugs = (info.node.children || []).map((c: any) => parseNodeKey(c.key).slug)
                                                        subAltKategoriSluglar = subAltKategoriSluglar.filter(x => !subSlugs.includes(x))

                                                        // Parent kategori: altında başka seçim kalmadıysa kaldır (mevcut yaklaşımın gibi)
                                                        const parentKat = String(info.node.parentKey)
                                                        const stillHasAnyUnderParent = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                            const ks = String(k)
                                                            // alt veya sub key içinde parent kategori slug geçiyor mu?
                                                            return ks.includes(`*${parentKat}`) || ks.endsWith(`*${parentKat}`) || ks === parentKat
                                                        })
                                                        if (!stillHasAnyUnderParent) {
                                                            kategoriSluglar = kategoriSluglar.filter(x => x !== parentKat)
                                                        }
                                                    }
                                                } else {
                                                    // Leaf kaldırıldı
                                                    if (parsed.type === 'SUB') {
                                                        const subSlug = parsed.slug
                                                        const parts = parsed.parts
                                                        const parentAltSlug = parts[3]
                                                        const parentKatSlug = parts[5]

                                                        subAltKategoriSluglar = subAltKategoriSluglar.filter(x => x !== subSlug)

                                                        // aynı alt altında başka sub seçili mi?
                                                        const stillHasSubUnderAlt = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                            const pk = parseNodeKey(k)
                                                            return pk.type === 'SUB' && pk.parts?.[3] === parentAltSlug
                                                        })

                                                        // alt node kendisi seçili mi?
                                                        const stillAltChecked = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                            const pk = parseNodeKey(k)
                                                            return pk.type === 'ALT' && pk.slug === parentAltSlug
                                                        })

                                                        if (!stillHasSubUnderAlt && !stillAltChecked) {
                                                            altKategoriSluglar = altKategoriSluglar.filter(x => x !== parentAltSlug)
                                                        }

                                                        // kategori: altında başka seçim yoksa kaldır
                                                        const stillHasAnyUnderKat = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                            const ks = String(k)
                                                            return ks.includes(`*${parentKatSlug}`) || ks === parentKatSlug
                                                        })
                                                        if (!stillHasAnyUnderKat) {
                                                            kategoriSluglar = kategoriSluglar.filter(x => x !== parentKatSlug)
                                                        }
                                                    } else {
                                                        const removedAltSlug = nodeKeyStr.split('*')[0]
                                                        altKategoriSluglar = altKategoriSluglar.filter(x => x !== removedAltSlug)
                                                    }
                                                }
                                            }

                                            setCurrentPage(1)
                                            setFilters((prevFilters) => ({
                                                ...prevFilters,
                                                kategori_slug: Array.from(new Set(kategoriSluglar)),
                                                alt_kategori_slug: Array.from(new Set(altKategoriSluglar)),
                                                sub_alt_kategori_slug: Array.from(new Set(subAltKategoriSluglar)), // ✅
                                                sayfa: 0,
                                            }))

                                            // İstersen URL de burada güncellenir (sen şu an category'de güncellemiyorsun)
                                            updateQueryParam({
                                                kategori_slug: kategoriSluglar.length ? kategoriSluglar.join(',') : null,
                                                alt_kategori_slug: altKategoriSluglar.length ? altKategoriSluglar.join(',') : null,
                                                sub_alt_kategori_slug: subAltKategoriSluglar.length ? subAltKategoriSluglar.join(',') : null,
                                                sayfa: '0',
                                            }, location, navigate)

                                            setCheckedKeys(updatedCheckedKeys)
                                        }}
                                    />
                                    <OkulyonetimOtherFilter
                                        title="Marka"
                                        searchbar
                                        checkedTempKeys={checkedYayineviKeys.map((key) => key.toString())}
                                        options={
                                            data?.okul?.okulyonetimUrunler?.yayinevleri
                                                ?.filter((yayinevi) => yayinevi !== null && yayinevi?.yayinevi_count > 0)
                                                ?.map((yayinevi) => ({
                                                    title: yayinevi?.isim || 'Diğerleri',
                                                    count: yayinevi?.yayinevi_count,
                                                    value: yayinevi?.yayinevi_slug || '', // Null değerlerden kaçının
                                                    isSelected: yayinevi?.isSelected,
                                                })) || []
                                        }
                                        onChange={(selectedYayineviSlugs) => {
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => {
                                                // Mevcut slug değerlerini al
                                                const currentSlugs = prevFilters.yayinevi_slug || []

                                                // Gelen slug değerlerini kontrol et ve güncelle
                                                const updatedSlugs = selectedYayineviSlugs.reduce((acc, slug) => {
                                                    if (currentSlugs.includes(slug)) {
                                                        // Eğer mevcutta varsa, çıkar
                                                        return acc.filter((existingSlug) => existingSlug !== slug)
                                                    } else {
                                                        // Eğer mevcutta yoksa, ekle
                                                        return [...acc, slug]
                                                    }
                                                }, currentSlugs)
                                                updateQueryParam({
                                                    yayinevi_slug: updatedSlugs.length > 0 ? updatedSlugs.join(',') : null,
                                                    sayfa: '0'
                                                }, location, navigate);

                                                setCheckedYayineviKeys(updatedSlugs as React.Key[])
                                                return {
                                                    ...prevFilters,
                                                    yayinevi_slug: updatedSlugs,
                                                    sayfa: 0,
                                                }
                                            })
                                        }}
                                    />
                                    <OkulyonetimSinifFilter
                                        title="Sınıf Seviyesi"
                                        searchbar={false}
                                        checkedTempKeys={checkedSinifKeys.map((key) => key.toString())}
                                        options={
                                            data?.okul?.okulyonetimUrunler?.siniflar
                                                ?.filter((sinif) => sinif !== null && sinif?.sinif_count > 0)
                                                ?.map((sinif) => ({
                                                    title: sinif?.isim || 'Diğerleri',
                                                    count: sinif?.sinif_count,
                                                    value: sinif?.sinif_slug || '', // Null değerlerden kaçının
                                                    isSelected: false,
                                                })) || []
                                        }
                                        onChange={(selectedSinifSlugs) => {
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => {
                                                // Mevcut slug değerlerini al
                                                const currentSlugs = prevFilters.sinif_slug || []

                                                // Gelen slug değerlerini kontrol et ve güncelle
                                                const updatedSlugs = selectedSinifSlugs.reduce((acc, slug) => {
                                                    if (currentSlugs.includes(slug)) {
                                                        // Eğer mevcutta varsa, çıkar
                                                        return acc.filter((existingSlug) => existingSlug !== slug)
                                                    } else {
                                                        // Eğer mevcutta yoksa, ekle
                                                        return [...acc, slug]
                                                    }
                                                }, currentSlugs)
                                                updateQueryParam({
                                                    sinif_slug: updatedSlugs.length > 0 ? updatedSlugs.join(',') : null,
                                                    sayfa: '0'
                                                }, location, navigate);

                                                setCheckedSinifKeys(updatedSlugs as React.Key[])
                                                return {
                                                    ...prevFilters,
                                                    sinif_slug: updatedSlugs,
                                                    sayfa: 0,
                                                }
                                            })
                                        }}
                                    />
                                    <OkulyonetimDersFilter
                                        title="Ders"
                                        searchbar={false}
                                        checkedTempKeys={checkedDersKeys.map((key) => key.toString())}
                                        options={
                                            data?.okul?.okulyonetimUrunler?.dersler
                                                ?.filter((ders) => ders !== null && ders?.ders_count > 0)
                                                ?.map((ders) => ({
                                                    title: ders?.isim || 'Diğerleri',
                                                    count: ders?.ders_count,
                                                    value: ders?.ders_slug || '', // Null değerlerden kaçının
                                                    isSelected: false,
                                                })) || []
                                        }
                                        onChange={(selectedDersSlugs) => {
                                            setCurrentPage(1)
                                            setFilters((prevFilters) => {
                                                // Mevcut slug değerlerini al
                                                const currentSlugs = prevFilters.ders_slug || []

                                                // Gelen slug değerlerini kontrol et ve güncelle
                                                const updatedSlugs = selectedDersSlugs.reduce((acc, slug) => {
                                                    if (currentSlugs.includes(slug)) {
                                                        // Eğer mevcutta varsa, çıkar
                                                        return acc.filter((existingSlug) => existingSlug !== slug)
                                                    } else {
                                                        // Eğer mevcutta yoksa, ekle
                                                        return [...acc, slug]
                                                    }
                                                }, currentSlugs)
                                                updateQueryParam({
                                                    ders_slug: updatedSlugs.length > 0 ? updatedSlugs.join(',') : null,
                                                    sayfa: '0'
                                                }, location, navigate);

                                                setCheckedDersKeys(updatedSlugs as React.Key[])
                                                return {
                                                    ...prevFilters,
                                                    ders_slug: updatedSlugs,
                                                    sayfa: 0,
                                                }
                                            })
                                        }}
                                    />
                                    <div className="okulyonetim-filter-toggle">
                                        <label>
                                            <input
                                                type="checkbox"
                                                checked={filters.yeni_mufredat!}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;

                                                    // filtreyi güncelle
                                                    setFilters((prevFilters) => ({
                                                        ...prevFilters,
                                                        yeni_mufredat: checked,
                                                        sayfa: 0,
                                                    }));
                                                    updateQueryParam(
                                                        { yeni_mufredat: checked ? 'true' : null },
                                                        location,
                                                        navigate
                                                    );

                                                }}
                                            />
                                            Sadece Yeni Müfredat Ürünleri
                                        </label>
                                    </div>
                                </Collapse.Panel>
                            </Collapse>
                        </div>
                    ) : (
                        <div>
                            <OkulyonetimCategoryFilter
                                treeData={formatTreeData() as CustomTreeDataNode[]}
                                checkedKeys={checkedKeys} // Seçili kategoriler
                                onCheck={(updatedCheckedKeys, info) => {
                                    let kategoriSluglar: string[] = Array.isArray(filters?.kategori_slug) ? [...filters.kategori_slug] : []
                                    let altKategoriSluglar: string[] = Array.isArray(filters?.alt_kategori_slug) ? [...filters.alt_kategori_slug] : []
                                    let subAltKategoriSluglar: string[] = Array.isArray(filters?.sub_alt_kategori_slug) ? [...filters.sub_alt_kategori_slug] : []

                                    const nodeKeyStr = String(info.node.key)
                                    const parsed = parseNodeKey(info.node.key)

                                    if (info.checked) {
                                        if (info.node.children && info.node.children.length > 0) {
                                            // Parent node seçildi (Kategori veya Alt)
                                            if (parsed.type === 'KAT') {
                                                // Kategori seçildi -> kategori + alt + varsa sub-alt ekle
                                                kategoriSluglar = Array.from(new Set([...kategoriSluglar, nodeKeyStr]))

                                                const altKeys = info.node.children.map((child: any) => String(child.key))
                                                const altSlugs = altKeys.map((k: React.Key) => parseNodeKey(k).slug)
                                                altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, ...altSlugs]))

                                                // sub-alt children varsa hepsini topla
                                                const subSlugs: string[] = []
                                                info.node.children.forEach((altNode: any) => {
                                                    if (altNode.children?.length) {
                                                        altNode.children.forEach((subNode: any) => {
                                                            subSlugs.push(parseNodeKey(subNode.key).slug)
                                                        })
                                                    }
                                                })
                                                subAltKategoriSluglar = Array.from(new Set([...subAltKategoriSluglar, ...subSlugs]))
                                            } else if (parsed.type === 'ALT') {
                                                // Alt kategori node seçildi -> alt + parent kategori + sub-alt children
                                                const altSlug = parsed.slug
                                                altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, altSlug]))

                                                if (info.node.parentKey) {
                                                    kategoriSluglar = Array.from(new Set([...kategoriSluglar, String(info.node.parentKey)]))
                                                }

                                                const subSlugs = (info.node.children || []).map((c: any) => parseNodeKey(c.key).slug)
                                                subAltKategoriSluglar = Array.from(new Set([...subAltKategoriSluglar, ...subSlugs]))
                                            }
                                        } else {
                                            // Leaf seçildi (ALT leaf veya SUB leaf)
                                            if (parsed.type === 'SUB') {
                                                const subSlug = parsed.slug
                                                const parts = parsed.parts
                                                const parentAltSlug = parts[3]
                                                const parentKatSlug = parts[5]

                                                subAltKategoriSluglar = Array.from(new Set([...subAltKategoriSluglar, subSlug]))
                                                altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, parentAltSlug]))
                                                kategoriSluglar = Array.from(new Set([...kategoriSluglar, parentKatSlug]))
                                            } else {
                                                // ALT leaf (sub yok)
                                                const altSlug = nodeKeyStr.split('*')[0]
                                                altKategoriSluglar = Array.from(new Set([...altKategoriSluglar, altSlug]))

                                                if (info.node.parentKey) {
                                                    kategoriSluglar = Array.from(new Set([...kategoriSluglar, String(info.node.parentKey)]))
                                                }
                                            }
                                        }
                                    } else {
                                        if (info.node.children && info.node.children.length > 0) {
                                            // Parent kaldırıldı (Kategori veya Alt)
                                            if (parsed.type === 'KAT') {
                                                const katSlug = nodeKeyStr
                                                kategoriSluglar = kategoriSluglar.filter(x => x !== katSlug)

                                                // Bu kategorinin altlarını ve sublarını da kaldır
                                                const altKeys = info.node.children.map((child: any) => String(child.key))
                                                const altSlugs = altKeys.map((k: React.Key) => parseNodeKey(k).slug)
                                                altKategoriSluglar = altKategoriSluglar.filter(x => !altSlugs.includes(x))

                                                const subSlugs: string[] = []
                                                info.node.children.forEach((altNode: any) => {
                                                    if (altNode.children?.length) {
                                                        altNode.children.forEach((subNode: any) => subSlugs.push(parseNodeKey(subNode.key).slug))
                                                    }
                                                })
                                                subAltKategoriSluglar = subAltKategoriSluglar.filter(x => !subSlugs.includes(x))
                                            } else if (parsed.type === 'ALT') {
                                                const altSlug = parsed.slug
                                                altKategoriSluglar = altKategoriSluglar.filter(x => x !== altSlug)

                                                // Altın sub-altlarını da kaldır
                                                const subSlugs = (info.node.children || []).map((c: any) => parseNodeKey(c.key).slug)
                                                subAltKategoriSluglar = subAltKategoriSluglar.filter(x => !subSlugs.includes(x))

                                                // Parent kategori: altında başka seçim kalmadıysa kaldır (mevcut yaklaşımın gibi)
                                                const parentKat = String(info.node.parentKey)
                                                const stillHasAnyUnderParent = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                    const ks = String(k)
                                                    // alt veya sub key içinde parent kategori slug geçiyor mu?
                                                    return ks.includes(`*${parentKat}`) || ks.endsWith(`*${parentKat}`) || ks === parentKat
                                                })
                                                if (!stillHasAnyUnderParent) {
                                                    kategoriSluglar = kategoriSluglar.filter(x => x !== parentKat)
                                                }
                                            }
                                        } else {
                                            // Leaf kaldırıldı
                                            if (parsed.type === 'SUB') {
                                                const subSlug = parsed.slug
                                                const parts = parsed.parts
                                                const parentAltSlug = parts[3]
                                                const parentKatSlug = parts[5]

                                                subAltKategoriSluglar = subAltKategoriSluglar.filter(x => x !== subSlug)

                                                // aynı alt altında başka sub seçili mi?
                                                const stillHasSubUnderAlt = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                    const pk = parseNodeKey(k)
                                                    return pk.type === 'SUB' && pk.parts?.[3] === parentAltSlug
                                                })

                                                // alt node kendisi seçili mi?
                                                const stillAltChecked = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                    const pk = parseNodeKey(k)
                                                    return pk.type === 'ALT' && pk.slug === parentAltSlug
                                                })

                                                if (!stillHasSubUnderAlt && !stillAltChecked) {
                                                    altKategoriSluglar = altKategoriSluglar.filter(x => x !== parentAltSlug)
                                                }

                                                // kategori: altında başka seçim yoksa kaldır
                                                const stillHasAnyUnderKat = (updatedCheckedKeys as React.Key[]).some((k) => {
                                                    const ks = String(k)
                                                    return ks.includes(`*${parentKatSlug}`) || ks === parentKatSlug
                                                })
                                                if (!stillHasAnyUnderKat) {
                                                    kategoriSluglar = kategoriSluglar.filter(x => x !== parentKatSlug)
                                                }
                                            } else {
                                                const removedAltSlug = nodeKeyStr.split('*')[0]
                                                altKategoriSluglar = altKategoriSluglar.filter(x => x !== removedAltSlug)
                                            }
                                        }
                                    }

                                    setCurrentPage(1)
                                    setFilters((prevFilters) => ({
                                        ...prevFilters,
                                        kategori_slug: Array.from(new Set(kategoriSluglar)),
                                        alt_kategori_slug: Array.from(new Set(altKategoriSluglar)),
                                        sub_alt_kategori_slug: Array.from(new Set(subAltKategoriSluglar)), // ✅
                                        sayfa: 0,
                                    }))

                                    // İstersen URL de burada güncellenir (sen şu an category'de güncellemiyorsun)
                                    updateQueryParam({
                                        kategori_slug: kategoriSluglar.length ? kategoriSluglar.join(',') : null,
                                        alt_kategori_slug: altKategoriSluglar.length ? altKategoriSluglar.join(',') : null,
                                        sub_alt_kategori_slug: subAltKategoriSluglar.length ? subAltKategoriSluglar.join(',') : null,
                                        sayfa: '0',
                                    }, location, navigate)

                                    setCheckedKeys(updatedCheckedKeys)
                                }}
                            />

                            <OkulyonetimOtherFilter
                                title="Marka"
                                searchbar
                                checkedTempKeys={checkedYayineviKeys.map((key) => key.toString())}
                                options={
                                    data?.okul?.okulyonetimUrunler?.yayinevleri
                                        ?.filter((yayinevi) => yayinevi !== null && yayinevi?.yayinevi_count > 0)
                                        ?.map((yayinevi) => ({
                                            title: yayinevi?.isim || 'Diğerleri',
                                            count: yayinevi?.yayinevi_count,
                                            value: yayinevi?.yayinevi_slug || '', // Null değerlerden kaçının
                                            isSelected: yayinevi?.isSelected,
                                        })) || []
                                }
                                onChange={(selectedYayineviSlugs) => {
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => {
                                        // Mevcut slug değerlerini al
                                        const currentSlugs = prevFilters.yayinevi_slug || []

                                        // Gelen slug değerlerini kontrol et ve güncelle
                                        const updatedSlugs = selectedYayineviSlugs.reduce((acc, slug) => {
                                            if (currentSlugs.includes(slug)) {
                                                // Eğer mevcutta varsa, çıkar
                                                return acc.filter((existingSlug) => existingSlug !== slug)
                                            } else {
                                                // Eğer mevcutta yoksa, ekle
                                                return [...acc, slug]
                                            }
                                        }, currentSlugs)
                                        updateQueryParam({
                                            yayinevi_slug: updatedSlugs.length > 0 ? updatedSlugs.join(',') : null,
                                            sayfa: '0'
                                        }, location, navigate);
                                        setCheckedYayineviKeys(updatedSlugs as React.Key[])
                                        return {
                                            ...prevFilters,
                                            yayinevi_slug: updatedSlugs,
                                            sayfa: 0,
                                        }
                                    })
                                }}
                            />
                            <OkulyonetimOtherFilter
                                title="Sınıf Seviyesi"
                                searchbar={false}
                                checkedTempKeys={checkedSinifKeys.map((key) => key.toString())}
                                options={
                                    data?.okul?.okulyonetimUrunler?.siniflar
                                        ?.filter((sinif) => sinif !== null && sinif?.sinif_count > 0)
                                        ?.map((sinif) => ({
                                            title: sinif?.isim || 'Diğerleri',
                                            count: sinif?.sinif_count,
                                            value: sinif?.sinif_slug || '', // Null değerlerden kaçının
                                            isSelected: false,
                                        })) || []
                                }
                                onChange={(selectedSinifSlugs) => {
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => {
                                        // Mevcut slug değerlerini al
                                        const currentSlugs = prevFilters.sinif_slug || []

                                        // Gelen slug değerlerini kontrol et ve güncelle
                                        const updatedSlugs = selectedSinifSlugs.reduce((acc, slug) => {
                                            if (currentSlugs.includes(slug)) {
                                                // Eğer mevcutta varsa, çıkar
                                                return acc.filter((existingSlug) => existingSlug !== slug)
                                            } else {
                                                // Eğer mevcutta yoksa, ekle
                                                return [...acc, slug]
                                            }
                                        }, currentSlugs)
                                        updateQueryParam({
                                            sinif_slug: updatedSlugs.length > 0 ? updatedSlugs.join(',') : null,
                                            sayfa: '0'
                                        }, location, navigate);
                                        setCheckedSinifKeys(updatedSlugs as React.Key[])
                                        return {
                                            ...prevFilters,
                                            sinif_slug: updatedSlugs,
                                            sayfa: 0,
                                        }
                                    })
                                }}
                            />
                            <OkulyonetimOtherFilter
                                title="Ders"
                                searchbar={false}
                                checkedTempKeys={checkedDersKeys.map((key) => key.toString())}
                                options={
                                    data?.okul?.okulyonetimUrunler?.dersler
                                        ?.filter((ders) => ders !== null && ders?.ders_count > 0)
                                        ?.map((ders) => ({
                                            title: ders?.isim || 'Diğerleri',
                                            count: ders?.ders_count,
                                            value: ders?.ders_slug || '', // Null değerlerden kaçının
                                            isSelected: false,
                                        })) || []
                                }
                                onChange={(selectedDersSlugs) => {
                                    setCurrentPage(1)
                                    setFilters((prevFilters) => {
                                        // Mevcut slug değerlerini al
                                        const currentSlugs = prevFilters.ders_slug || []

                                        // Gelen slug değerlerini kontrol et ve güncelle
                                        const updatedSlugs = selectedDersSlugs.reduce((acc, slug) => {
                                            if (currentSlugs.includes(slug)) {
                                                // Eğer mevcutta varsa, çıkar
                                                return acc.filter((existingSlug) => existingSlug !== slug)
                                            } else {
                                                // Eğer mevcutta yoksa, ekle
                                                return [...acc, slug]
                                            }
                                        }, currentSlugs)
                                        updateQueryParam({
                                            ders_slug: updatedSlugs.length > 0 ? updatedSlugs.join(',') : null,
                                            sayfa: '0'
                                        }, location, navigate);
                                        setCheckedDersKeys(updatedSlugs as React.Key[])
                                        return {
                                            ...prevFilters,
                                            ders_slug: updatedSlugs,
                                            sayfa: 0,
                                        }
                                    })
                                }}
                            />

                            <div className="okulyonetim-filter-toggle">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={filters.yeni_mufredat!}
                                        onChange={(e) => {
                                            const checked = e.target.checked;

                                            // filtreyi güncelle
                                            setFilters((prevFilters) => ({
                                                ...prevFilters,
                                                yeni_mufredat: checked,
                                                sayfa: 0,
                                            }));

                                            // URL parametresini güncelle
                                            updateQueryParam(
                                                { yeni_mufredat: checked ? 'true' : null },
                                                location,
                                                navigate
                                            );
                                        }}
                                    />
                                    Sadece Yeni Müfredat Ürünleri
                                </label>
                            </div>
                        </div>
                    )}
                </Col>
                <Col xs={24} lg={18}>
                    <Row gutter={[16, 16]} className="okulyonetim-product-list-top">
                        {/* Seçtikleriniz */}
                        <Col xs={24} md={18}>
                            {isAnyFilterActive() && (
                                <div className="okulyonetim-selected-filters">
                                    <div className="filters-header">
                                        <span className="label">Seçtikleriniz:</span>
                                        <Button
                                            danger
                                            className="clear-filters-button"
                                            onClick={() => {
                                                // Tüm filtreleri temizle
                                                setFilters({
                                                    tip_slug: lastSlugPart || '',
                                                    kategori_slug: [],
                                                    alt_kategori_slug: [],
                                                    sub_alt_kategori_slug: [],
                                                    yayinevi_slug: [],
                                                    ders_slug: [],
                                                    sinif_slug: [],
                                                    sayfa: 0,
                                                    orderBy: 0,
                                                    yeni_mufredat: false,
                                                })
                                                setCheckedKeys([])
                                                setCheckedYayineviKeys([])
                                                setCheckedSinifKeys([])
                                                setCheckedDersKeys([])
                                                setCurrentPage(1)
                                            }}
                                        >
                                            Filtreleri Temizle
                                        </Button>

                                    </div>
                                    <div className="okulyonetim-selected-filters-head">
                                        {/* 1) Kategoriler */}
                                        {filters.kategori_slug?.map((slug) => (
                                            <span key={`kat-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        // Ana kategoriyi kaldır
                                                        const newKategoriSlugs = filters.kategori_slug?.filter((item) => item !== slug) || [];
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            kategori_slug: newKategoriSlugs,
                                                            alt_kategori_slug: [],
                                                            sayfa: 0,
                                                        }));
                                                        // **Tree'deki check durumunu tamamen kaldır**
                                                        setCheckedKeys([]);
                                                    }}
                                                >
                                                    x
                                                </button>
                                            </span>
                                        ))}
                                        {/* 2) Alt Kategoriler */}
                                        {filters.alt_kategori_slug?.map((slug) => (
                                            <span key={`altkat-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newAltKategoriSlugs =
                                                            filters.alt_kategori_slug?.filter((item) => item !== slug) || []

                                                        if (newAltKategoriSlugs.length > 0) {
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                alt_kategori_slug: newAltKategoriSlugs,
                                                                sayfa: 0,
                                                            }))
                                                            // Ağaçta da uncheck olsun isterseniz (uniqueKey’e dikkat!):
                                                            setCheckedKeys((prev) => prev.filter((k) => {
                                                                // Kayıtlar "alt_kategori_slug*index*parentSlug" şeklinde tutulabilir
                                                                // Dolayısıyla .split('*')[0] ile eşleşme kontrolü yapabilirsiniz.
                                                                const realSlug = typeof k === 'string' ? k.split('*')[0] : k
                                                                return realSlug !== slug
                                                            }))
                                                        } else {
                                                            setFilters((prev) => ({
                                                                ...prev,
                                                                alt_kategori_slug: newAltKategoriSlugs,
                                                                kategori_slug: [],
                                                                sayfa: 0,
                                                            }))
                                                            setCheckedKeys([]);
                                                        }

                                                    }}
                                                >x</button></span>
                                        ))}

                                        {/* 3) Dersler */}
                                        {filters.ders_slug?.map((slug) => (
                                            <span key={`ders-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newDersSlugs = filters.ders_slug?.filter((item) => item !== slug) || []
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            ders_slug: newDersSlugs,
                                                            sayfa: 0,
                                                        }))
                                                        setCheckedDersKeys((prev) => prev.filter((k) => k !== slug))
                                                    }}>x</button></span>
                                        ))}

                                        {/* 4) Sınıflar */}
                                        {filters.sinif_slug?.map((slug) => (
                                            <span key={`sinif-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newSinifSlugs = filters.sinif_slug?.filter((item) => item !== slug) || []
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            sinif_slug: newSinifSlugs,
                                                            sayfa: 0,
                                                        }))
                                                        setCheckedSinifKeys((prev) => prev.filter((k) => k !== slug))
                                                    }}>x</button></span>
                                        ))}

                                        {/* 5) Yayınevleri */}
                                        {filters.yayinevi_slug?.map((slug) => (
                                            <span key={`yay-${slug}`} className="selected-filter-chip">{slug}
                                                <button
                                                    onClick={() => {
                                                        const newYayineviSlugs = filters.yayinevi_slug?.filter((item) => item !== slug) || []
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            yayinevi_slug: newYayineviSlugs,
                                                            sayfa: 0,
                                                        }))
                                                        setCheckedYayineviKeys((prev) => prev.filter((k) => k !== slug))
                                                    }}>x</button></span>
                                        ))}

                                        {filters.yeni_mufredat && (
                                            <span className="selected-filter-chip">
                                                Yeni Müfredat
                                                <button
                                                    onClick={() => {
                                                        setFilters((prev) => ({
                                                            ...prev,
                                                            yeni_mufredat: false,
                                                            sayfa: 0,
                                                        }))
                                                    }}
                                                >
                                                  x
                                                </button>
                                              </span>
                                        )}

                                    </div>
                                </div>
                            )}
                        </Col>
                        {/* Sıralama vs. (Sizin mevcut kodunuz) */}
                        <Col xs={24} md={6}>
                            <div className="okulyonetim-sorting-view">
                                <span className="label">Sıralama: </span>
                                <Select
                                    value={getSelectValueFromOrderBy(filters.orderBy!)}
                                    onChange={(value) => {
                                        const selectedValue = value
                                        let newOrderBy = 0
                                        switch (selectedValue) {
                                            case 'fiyatArtan':
                                                newOrderBy = 1
                                                break
                                            case 'fiyatAzalan':
                                                newOrderBy = 2
                                                break
                                            case 'isimArtan':
                                                newOrderBy = 3
                                                break
                                            case 'isimAzalan':
                                                newOrderBy = 4
                                                break
                                            default:
                                                newOrderBy = 0
                                        }

                                        setFilters((prev) => ({
                                            ...prev,
                                            orderBy: newOrderBy,
                                            sayfa: 0,
                                        }))

                                        // sayfayı sıfırlıyorsanız
                                        setCurrentPage(1)
                                    }}
                                >
                                    <option value="onerilen"> Önerilen</option>
                                    <option value="fiyatArtan">Fiyata Göre Artan</option>
                                    <option value="fiyatAzalan">Fiyata Göre Azalan</option>
                                    <option value="isimArtan"> A'dan Z'ye</option>
                                    <option value="isimAzalan"> Z'den A'ya</option>
                                </Select>
                            </div>
                        </Col>
                    </Row>
                    <Row className="okulyonetim-product-list-row" gutter={[24, 24]} wrap={true}>
                        {data?.okul?.okulyonetimUrunler?.okulyonetim_urunler?.map((product, index) => (
                            <Col xs={24} sm={12} md={8} lg={6} key={index}>
                                <OkulyonetimProduct
                                    image={product?.urun?.resim ? product.urun.resim : product?.urun?.resim_url!}
                                    title={product?.urun?.isim!}
                                    pdf_url={product?.urun?.pdf_url}
                                    pdf={product?.urun?.pdf}
                                    id={product?.urun?.id ? product.urun.id : 1}
                                    price={product?.urun?.etiket_fiyati!}
                                    quantity={1}
                                    mufredat={product?.urun?.mufredat ? product.urun.mufredat : false}
                                    oneri={product?.urun?.oneri ? product.urun.oneri : false}
                                    initialIsFavori={product?.urun?.is_favori ? product.urun.is_favori : false}
                                />
                            </Col>
                        ))}
                    </Row>
                </Col>
            </Row>
            <Pagination
                current={currentPage}
                pageSize={20}
                total={data?.okul?.okulyonetimUrunler?.toplam_urun_sayisi!}
                onChange={handlePageChange}
                style={{marginTop: 16, textAlign: 'center'}}
                showSizeChanger={false}
            />
        </div>
    )
}

export default OkulyonetimProductList
